import { DOCUMENT_TYPE } from '@/features/documents/constants';
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/api';
import type { ConflictFinding, ConflictsAnalysis } from '@/features/conflicts/types';
import {
  ANALYSIS_TERMINAL_STATUSES,
  AnalysisStatus,
  type WorkspaceType,
} from '@/features/workspaces/constants';
import { parseStreamRow, queryStream, type StreamRowPayload } from '@/features/workspaces/stream';
import { resourceLatestVersionQuery } from '@/features/workspaces/queries';
import { EvaluationConflictClaimStatus } from '@/features/conflicts/constants';

export const CONFLICTS_QUERY_KEY = 'conflicts' as const;

const SEVERITY_ORDER: Record<string, number> = { HIGH: 3, MEDIUM: 2, LOW: 1 };
const CONFLICT_STATUS: Record<EvaluationConflictClaimStatus, AnalysisStatus> = {
  [EvaluationConflictClaimStatus.PENDING]: AnalysisStatus.NOT_STARTED,
  [EvaluationConflictClaimStatus.IN_PROGRESS]: AnalysisStatus.IN_PROGRESS,
  [EvaluationConflictClaimStatus.COMPLETED]: AnalysisStatus.DONE,
  [EvaluationConflictClaimStatus.FAILED]: AnalysisStatus.FAILED,
};

const severityOrder = (severity?: string | null) =>
  severity ? (SEVERITY_ORDER[severity.toUpperCase()] ?? 0) : 0;

export const conflictsQuery = (type: WorkspaceType, id: string) => {
  const docType = DOCUMENT_TYPE[type];
  const queryKey = [CONFLICTS_QUERY_KEY, type, id] as const;
  return queryOptions({
    queryKey,
    queryFn: async (): Promise<ConflictsAnalysis | null> => {
      const response = await api.get<
        Omit<ConflictsAnalysis, 'findings' | 'status'> & {
          status: EvaluationConflictClaimStatus;
          conflicts: { findings: ConflictFinding[] }[];
        }
      >('v1/analysis/segment_inconsistencies', {
        params: { type: docType, resourceId: id },
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });
      if (response.status === 404) return null;
      const { conflicts, ...analysis } = response.data;
      return {
        ...analysis,
        status: CONFLICT_STATUS[analysis.status],
        findings: conflicts
          .flatMap((group) => group.findings)
          .sort((a, b) => severityOrder(b.severity) - severityOrder(a.severity)),
      };
    },
    meta: {
      stream: queryStream<
        ConflictsAnalysis | null,
        StreamRowPayload<EvaluationConflictClaimStatus>
      >({
        topic: 'conflict_finder',
        type: docType,
        resourceId: id,
        isActive: (data) =>
          data?.status != null && !ANALYSIS_TERMINAL_STATUSES.includes(data.status),
        onStatusUpdate: (payload, queryClient) => {
          const invalidate = () => {
            queryClient.invalidateQueries({ queryKey });
            queryClient.invalidateQueries(resourceLatestVersionQuery(id));
          };
          const row = parseStreamRow(payload);
          if (row === 'refetch') return invalidate();
          if (!row) return;
          const status = CONFLICT_STATUS[row.status];

          const current = queryClient.getQueryData<ConflictsAnalysis | null>(queryKey);
          if (
            !current ||
            current.conflictId !== row.rowId ||
            ANALYSIS_TERMINAL_STATUSES.includes(status)
          )
            return invalidate();

          queryClient.setQueryData<ConflictsAnalysis | null>(
            queryKey,
            (old) => old && { ...old, status, progress: row.progress },
          );
        },
      }),
    },
  });
};
